import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, NavigationExtras } from '@angular/router';

import { Subscription } from 'rxjs/Subscription';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { empty } from 'rxjs/observable/empty';
import { first } from 'rxjs/operators/first';
import { share } from 'rxjs/operators/share';
import { startWith } from 'rxjs/operators/startWith';
import { takeUntil } from 'rxjs/operators/takeUntil';
import { zip } from 'rxjs/operators/zip';

import { ApplicationService, BasicStudentInfo } from '../../services/application/application.service';
import { AuthService } from '../../services/authentication/auth.service';
import { DialogService } from '../../services/dialogs/dialogs.service';
import { ErrorService } from '../../services/error/error.service';
import { NotificationService, NotificationItem } from '../../services/notification/notification.service';
import { SplashService } from '../../services/splash/splash.service';
import { UserService, UserInfo } from '../../services/user/user.service';

@Component({
    selector: 'app-home-node',
    styleUrls: ['home.component.css'],
    templateUrl: 'home.component.html',
})
export class HomeComponent implements OnInit, OnDestroy {
    private _subRelease: Subject<void> = new Subject<void>();

    AdoptionRequests: NotificationItem[];
    ClassicNotification: boolean;
    CurrentNotifications: NotificationItem[];
    LastLogin: Date;
    PartialViewReady: boolean;
    ShowSummerRolloverNote: boolean;
    StudentCount: number;
    User: UserInfo;

    constructor(
        private userService: UserService,
        private splashService: SplashService,
        private dialogService: DialogService,
        private authService: AuthService,
        private appService: ApplicationService,
        private errorService: ErrorService,
        private router: Router,
        private notificationService: NotificationService
    ) {}

    ngOnDestroy() {
        this._subRelease.next();
        this._subRelease.complete();
    }

    ngOnInit() {
        this.ClassicNotification = true;
        this.CurrentNotifications = null;
        this.PartialViewReady = false;
        this.ShowSummerRolloverNote = this.appService.ShowSummerRolloverNote;

        // Wait for the first non-null user object with an authtoken before proceeding
        this.userService.UserSubject.pipe(
            takeUntil(this._subRelease),
            first(_user => _user && !!_user.AuthToken)
        ).subscribe(user => {
            this.User = user;

            // Set up constant monitoring of the user object, to catch AuthToken updates. If there's a role change, we
            // need to update the notifications and possibly the students for the parent as well.
            this.userService.UserSubject.pipe(takeUntil(this._subRelease)).subscribe(_user => {
                this.User = _user;

                // Exit out in case the auth token is missing, as it is likely a logout event.
                if (!this.User || !this.User.AuthToken) {
                    return;
                }

                // Poll for data used for the home screen display
                const curNoteSub = this.notificationService.GetNotifications(this.User.AuthToken, this.User.CurrentRole);

                // If this is a parent, we need to retrieve their list of student children.
                const parentStudentsSub: Observable<BasicStudentInfo[]> = this.User.IsParent
                    ? this.appService.GetStudentsForParent(this.User.AuthToken)
                    : empty().pipe(startWith(<BasicStudentInfo[]>null));

                combineLatest(curNoteSub, parentStudentsSub, this.appService.GetAllSchoolYears(), function(cNote, parStus, years) {
                    return {
                        CurrentNotes: cNote,
                        ParentStudents: parStus,
                        Years: years,
                    };
                })
                    .pipe(takeUntil(this._subRelease))
                    .subscribe(
                        data => {
                            if (data.CurrentNotes != null) {
                                // Parse through the list and route entries as needed to the notifications list.
                                const notes = data.CurrentNotes;

                                this.LastLogin = notes.LastLogin;
                                this.CurrentNotifications = notes.Notices || [];
                                this.ClassicNotification = notes.ClassicNotification == null ? true : notes.ClassicNotification;
                                this.StudentCount = notes.StudentCount || 0;
                                this.AdoptionRequests = notes.AdoptionRequests || [];
                            }

                            if (data.Years && data.Years.length && !this.appService.SelectedSchoolYear) {
                                this.appService.SelectedSchoolYear = data.Years[0];
                            }

                            // If there are no children found and this is a parent going to the home page, alert and
                            // gently log out the user.
                            if (this.User.IsParent && (!data.ParentStudents || !data.ParentStudents.length)) {
                                this.dialogService
                                    .OpenMessageBoxDialog({
                                        TitleText: 'No Students Found',
                                        MessageText:
                                            `There currently do not appear to be any students assigned to your DELTA ` +
                                            `account. The DELTA System is only available to parents who have enrolled/attached ` +
                                            `students within the Focus parent portal and are configured to have access to their ` +
                                            `information. If you need assistance with the DELTA System, please contact your student's ` +
                                            `school counselor at their current school. You will now be logged out of the system. ` +
                                            `We apologize for the inconvenience.`,
                                    })
                                    .subscribe(result => {
                                        this.authService.Logout(false, null);
                                    });
                                return;
                            } else if (this.User.IsParent && data.ParentStudents && !!data.ParentStudents.length) {
                                // For parents, check to see if we have a TargetStudent value for the user. That would be from Focus SSO.
                                // If that TargetStudent isn't found in the list we just retrieved, that could be an issue, but for now
                                // just preselect their first possible student child.
                                let setStudent = data.ParentStudents[0];

                                if (this.User.TargetStudentID) {
                                    const foundStudent = data.ParentStudents.find(s => s.StudentID === this.User.TargetStudentID);

                                    if (foundStudent) {
                                        setStudent = foundStudent;
                                    }
                                }

                                this.appService.SetSelectedStudent(this.User.AuthToken, setStudent);
                            } else {
                                // If not a parent under the approve situations, clear the selected student
                                this.appService.ClearSelectedStudent();
                            }

                            this.splashService.CloseSplash();
                            this.PartialViewReady = true;
                        },
                        error => {
                            this.errorService.HandleServiceError(error);
                        }
                    );
            });
        });
    }

    // #region Go To Route Method

    GoToRoute(navroute: any[], options?: NavigationExtras) {
        if (!navroute || !navroute.length) {
            return;
        }

        this.router.navigate(navroute);
    }

    // #endregion

    // #region Show Notification Details Method

    ShowNotificationDetails(note: NotificationItem) {
        if (note == null || !note.Details || !note.Details.length) {
            return;
        }

        const self = this;

        this.dialogService
            .OpenClassicNotificationDialog({
                TitleText: 'Notification Details',
                MessageText: `The following Dual Enrollment records are mentioned in this notification:`,
                Details: note.Details,
                IsAdvisor: self.User.IsAdvisor,
            })
            .subscribe(chosen => {
                if (chosen) {
                    // User chose a record to display. Transition to the view state with those details set.
                    // Set up the selected student as the active student and return to the correct referring home page.
                    self.splashService.DisplaySplash({
                        TitleText: 'Please wait',
                        MessageText: `Loading page for ${chosen.StudentName}...`,
                    });

                    if (self.User && (self.User.IsStudent || self.User.IsParent)) {
                        self.GoToRoute(['/studentview', chosen.DualEnrollStudentID, chosen.StudentID]);
                    } else {
                        self.GoToRoute(['/studentview', chosen.StudentID]);
                    }
                }
            });
    }

    // #endregion

    // #region Show Notification Overview Method

    ShowNotificationOverview(isAdoption?: boolean) {
        const self = this;

        const msg =
            isAdoption === true
                ? `The following Dual Enrollment Course Adoption Requests`
                : `The following Dual Enrollment notifications`;

        this.dialogService
            .OpenNotificationDialog({
                TitleText: 'Notification Details',
                MessageText: msg + ` are available for your attention:`,
                Notifications: isAdoption === true ? self.AdoptionRequests : self.CurrentNotifications,
                IsAdoptions: isAdoption,
                IsAdvisor: self.User.IsAdvisor,
            })
            .subscribe(chosen => {
                if (chosen) {
                    // User chose a record to display. Transition to the view state with those details set.

                    if (isAdoption === true) {
                        self.GoToRoute(['/manageadoptions']);
                    } else {
                        // Set up the selected student as the active student and return to the correct referring home page.
                        self.splashService.DisplaySplash({
                            TitleText: 'Please wait',
                            MessageText: `Loading page for ${chosen.StudentName}...`,
                        });

                        if (chosen.DualEnrollStudentID != null) {
                            self.GoToRoute(['/studentview', chosen.DualEnrollStudentID, chosen.StudentID]);
                        } else {
                            self.GoToRoute(['/studentview', chosen.StudentID]);
                        }
                    }
                }
            });
    }

    // #endregion
}



// WEBPACK FOOTER //
// ./src/app/home/home.component.ts